import { useStackModal } from '@rango-dev/internal-profile/hooks';
import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import ConnectWalletModal from '../components/ConnectWalletModal';
import { ProfileWalletsModal } from '../components/Profile/ProfileWalletsModal';
import { CONNECT_WALLETS_MODAL_ID, PROFILE_WALLETS_MODAL_ID } from '../constants/modals';
import { shouldExcludeWalletInProfileWalletsList } from '../utils/wallets/general';

import { useProfile } from './useProfile';

type UseProfileWalletFlow = {
  readyToSign: boolean;
  openProfileWalletFlow: () => void;
};

/*
 * Drives the profile login flow: the profile wallets modal when a compatible wallet
 * is already connected, otherwise the connect wallet modal, with the two chained as
 * the user moves between them. Shared by every entry point into the flow so they all
 * behave the same.
 */
export function useProfileWalletFlow(): UseProfileWalletFlow {
  const { readyToSign } = useProfile();
  const { push, pop, stack, clear } = useStackModal();
  const { t } = useTranslation();

  const shouldOpenConnectWalletsModalRef = useRef(false);
  const shouldOpenProfileWalletsModalRef = useRef(false);
  /*
   * Several components can hold this hook at once, so the modal is only closed again
   * by whichever instance opened it.
   */
  const ownsProfileWalletsModalRef = useRef(false);
  const lastStackItemId = stack[stack.length - 1]?.id;

  const setShouldOpenConnectWalletsModal = (shouldOpenConnectWalletsModal?: boolean) => {
    shouldOpenConnectWalletsModalRef.current = !!shouldOpenConnectWalletsModal;
  };

  const setShouldOpenProfileWalletsModal = (shouldOpenProfileWalletsModal?: boolean) => {
    shouldOpenProfileWalletsModalRef.current = !!shouldOpenProfileWalletsModal;
  };

  const openProfileWalletsModal = () => {
    ownsProfileWalletsModalRef.current = true;
    push({
      id: PROFILE_WALLETS_MODAL_ID,
      renderContent: (index) => (
        <ProfileWalletsModal
          indexInStack={index}
          setShouldOpenConnectWalletsModal={setShouldOpenConnectWalletsModal}
        />
      ),
      onExit: () => {
        ownsProfileWalletsModalRef.current = false;
        if (shouldOpenConnectWalletsModalRef.current) {
          shouldOpenConnectWalletsModalRef.current = false;
          openConnectWalletsModal();
        }
      },
    });
  };

  const openConnectWalletsModal = () => {
    push({
      id: CONNECT_WALLETS_MODAL_ID,
      renderContent: (index) => (
        <ConnectWalletModal
          indexInStack={index}
          shouldExcludeWallet={shouldExcludeWalletInProfileWalletsList}
          title={t('Choose wallet for your profile')}
          afterConnect={() => {
            setShouldOpenProfileWalletsModal(true);
            clear();
          }}
          onDetachedConfirm={() => {
            setShouldOpenProfileWalletsModal(true);
            clear();
          }}
          detachedConfirmText={t('Continue')}
        />
      ),
      onExit: () => {
        if (shouldOpenProfileWalletsModalRef.current) {
          shouldOpenProfileWalletsModalRef.current = false;
          openProfileWalletsModal();
        }
      },
    });
  };

  const openProfileWalletFlow = () => {
    if (!readyToSign) {
      openConnectWalletsModal();
    } else {
      openProfileWalletsModal();
    }
  };

  // Close the profile wallets modal again if the last compatible wallet goes away.
  useEffect(() => {
    if (
      !readyToSign &&
      ownsProfileWalletsModalRef.current &&
      lastStackItemId === PROFILE_WALLETS_MODAL_ID
    ) {
      pop();
    }
  }, [readyToSign]);

  return { readyToSign, openProfileWalletFlow };
}
